import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Phone } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import AnimatedSection from '../components/AnimatedSection';
import { useLanguage } from '../i18n/pageTranslations';

const Otoplastia = () => {
  const lang = useLanguage();

  const texts = {
    es: {
      eyebrow: 'Cirugía Facial',
      title: 'Otoplastia',
      description: 'La otoplastia es la intervención que corrige la forma o la proyección de las orejas —habitualmente las llamadas "orejas despegadas"— para lograr un contorno natural y armónico con el rostro.',
      detail1: 'El tratamiento se realiza con anestesia local, de forma ambulatoria y sin necesidad de hospitalización: el paciente vuelve a casa el mismo día. Para quienes lo prefieran, puede realizarse también con sedación, ganando en confort durante la intervención.',
      detail2: 'Según el caso particular, la corrección se lleva a cabo a través de una incisión cutánea oculta detrás de la oreja o mediante mínimas incisiones en la cara anterior, con cicatrices prácticamente imperceptibles.',
      detail3: 'Los cuidados posteriores son sencillos: durante unos días se utiliza una felpa elástica de protección y, en los casos que lo precisen, medicación antibiótica y analgésica. La incorporación a la vida cotidiana es rápida.',
      requestAssessment: 'Solicitar Valoración',
      callNow: 'Llamar Ahora',
      realResults: 'Resultados reales de Otoplastia',
      immediateResult: 'Resultado inmediato',
      before: 'Antes',
      after: 'Después',
      realCases: 'Casos reales - Resultados pueden variar según cada paciente',
      benefitsTitle: 'Beneficios',
      resultsTitle: 'Resultados de la cirugía',
      benefits: [
        'Corrección permanente en una sola sesión',
        'Anestesia local con sedación',
        'Técnica mínimamente invasiva',
        'Cicatrices ocultas detrás de la oreja',
        'Recuperación rápida (7-10 días)',
        'Resultados naturales y duraderos',
      ],
      candidatesTitle: 'Candidatos',
      candidatesQuestion: '¿Es la otoplastia adecuada para usted?',
      candidates: [
        'Orejas prominentes o "de soplillo"',
        'Asimetría entre ambas orejas',
        'Orejas con forma irregular',
        'Adultos y niños a partir de 6 años',
        'Buena salud general',
      ],
      ctaTitle: '¿Desea más información?',
      ctaDesc: 'Solicite una consulta de valoración personalizada.',
      contact: 'Contactar',
    },
    en: {
      eyebrow: 'Facial Surgery',
      title: 'Otoplasty',
      description: 'Otoplasty is the procedure that corrects the shape or projection of the ears —commonly known as "protruding ears"— to achieve a natural and harmonious contour with the face.',
      detail1: 'The treatment is performed under local anesthesia, on an outpatient basis without hospitalization: the patient goes home the same day. For those who prefer, it can also be performed with sedation, gaining comfort during the procedure.',
      detail2: 'Depending on the particular case, the correction is carried out through a hidden skin incision behind the ear or through minimal incisions on the front, with virtually imperceptible scars.',
      detail3: 'Post-operative care is straightforward: an elastic protective headband is worn for a few days and, when required, antibiotic and analgesic medication. Return to daily life is quick.',
      requestAssessment: 'Request Assessment',
      callNow: 'Call Now',
      realResults: 'Real Otoplasty Results',
      immediateResult: 'Immediate result',
      before: 'Before',
      after: 'After',
      realCases: 'Real cases - Results may vary for each patient',
      benefitsTitle: 'Benefits',
      resultsTitle: 'Surgery results',
      benefits: [
        'Permanent correction in a single session',
        'Local anesthesia with sedation',
        'Minimally invasive technique',
        'Scars hidden behind the ear',
        'Quick recovery (7-10 days)',
        'Natural and lasting results',
      ],
      candidatesTitle: 'Candidates',
      candidatesQuestion: 'Is otoplasty right for you?',
      candidates: [
        'Prominent or "bat" ears',
        'Asymmetry between both ears',
        'Irregularly shaped ears',
        'Adults and children from 6 years old',
        'Good general health',
      ],
      ctaTitle: 'Want more information?',
      ctaDesc: 'Request a personalized assessment consultation.',
      contact: 'Contact',
    },
    fr: {
      eyebrow: 'Chirurgie Faciale',
      title: 'Otoplastie',
      description: 'L\'otoplastie est l\'intervention qui corrige la forme ou la projection des oreilles —habituellement appelées "oreilles décollées"— pour obtenir un contour naturel et harmonieux avec le visage.',
      detail1: 'Le traitement est réalisé sous anesthésie locale, en ambulatoire et sans hospitalisation : le patient rentre chez lui le jour même. Pour ceux qui le préfèrent, il peut également être réalisé sous sédation, offrant plus de confort pendant l\'intervention.',
      detail2: 'Selon le cas particulier, la correction est effectuée par une incision cutanée cachée derrière l\'oreille ou par de minimes incisions sur la face antérieure, avec des cicatrices pratiquement imperceptibles.',
      detail3: 'Les soins postopératoires sont simples : un bandeau élastique de protection est porté pendant quelques jours et, dans les cas qui le nécessitent, une médication antibiotique et analgésique. La reprise de la vie quotidienne est rapide.',
      requestAssessment: 'Demander une Évaluation',
      callNow: 'Appeler Maintenant',
      realResults: 'Résultats réels d\'Otoplastie',
      immediateResult: 'Résultat immédiat',
      before: 'Avant',
      after: 'Après',
      realCases: 'Cas réels - Les résultats peuvent varier selon chaque patient',
      benefitsTitle: 'Avantages',
      resultsTitle: 'Résultats de la chirurgie',
      benefits: [
        'Correction permanente en une seule séance',
        'Anesthésie locale avec sédation',
        'Technique peu invasive',
        'Cicatrices cachées derrière l\'oreille',
        'Récupération rapide (7-10 jours)',
        'Résultats naturels et durables',
      ],
      candidatesTitle: 'Candidats',
      candidatesQuestion: 'L\'otoplastie est-elle faite pour vous?',
      candidates: [
        'Oreilles décollées',
        'Asymétrie entre les deux oreilles',
        'Oreilles de forme irrégulière',
        'Adultes et enfants à partir de 6 ans',
        'Bonne santé générale',
      ],
      ctaTitle: 'Vous voulez plus d\'informations?',
      ctaDesc: 'Demandez une consultation d\'évaluation personnalisée.',
      contact: 'Contacter',
    },
    pt: {
      eyebrow: 'Cirurgia Facial',
      title: 'Otoplastia',
      description: 'A otoplastia é a intervenção que corrige a forma ou a projeção das orelhas —habitualmente chamadas "orelhas de abano"— para conseguir um contorno natural e harmônico com o rosto.',
      detail1: 'O tratamento é realizado com anestesia local, de forma ambulatorial e sem necessidade de hospitalização: o paciente volta para casa no mesmo dia. Para quem preferir, pode ser realizado também com sedação, ganhando em conforto durante a intervenção.',
      detail2: 'Segundo o caso particular, a correção é realizada através de uma incisão cutânea oculta atrás da orelha ou mediante mínimas incisões na face anterior, com cicatrizes praticamente imperceptíveis.',
      detail3: 'Os cuidados posteriores são simples: durante alguns dias utiliza-se uma faixa elástica de proteção e, nos casos que necessitem, medicação antibiótica e analgésica. A incorporação à vida cotidiana é rápida.',
      requestAssessment: 'Solicitar Avaliação',
      callNow: 'Ligar Agora',
      realResults: 'Resultados reais de Otoplastia',
      immediateResult: 'Resultado imediato',
      before: 'Antes',
      after: 'Depois',
      realCases: 'Casos reais - Resultados podem variar segundo cada paciente',
      benefitsTitle: 'Benefícios',
      resultsTitle: 'Resultados da cirurgia',
      benefits: [
        'Correção permanente em uma única sessão',
        'Anestesia local com sedação',
        'Técnica minimamente invasiva',
        'Cicatrizes ocultas atrás da orelha',
        'Recuperação rápida (7-10 dias)',
        'Resultados naturais e duradouros',
      ],
      candidatesTitle: 'Candidatos',
      candidatesQuestion: 'A otoplastia é adequada para você?',
      candidates: [
        'Orelhas proeminentes ou "de abano"',
        'Assimetria entre ambas as orelhas',
        'Orelhas com forma irregular',
        'Adultos e crianças a partir de 6 anos',
        'Boa saúde geral',
      ],
      ctaTitle: 'Deseja mais informações?',
      ctaDesc: 'Solicite uma consulta de avaliação personalizada.',
      contact: 'Contatar',
    },
  };

  const t = texts[lang] || texts.es;

  return (
    <div className="min-h-screen bg-[#FDFBF8]">
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#FDFBF8]">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection animation="fadeRight">
              <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{t.eyebrow}</p>
              <h1 className="font-serif text-5xl md:text-6xl text-[#1C1C1A] mb-6 leading-tight">{t.title}</h1>
              <div className="w-20 h-[2px] bg-[#B8975A] mb-8"></div>
              <p className="text-lg text-[#4A4845] leading-relaxed mb-4">{t.description}</p>
              <p className="text-sm text-[#4A4845] leading-relaxed mb-2">{t.detail1}</p>
              <p className="text-sm text-[#4A4845] leading-relaxed mb-2">{t.detail2}</p>
              <p className="text-sm text-[#4A4845] leading-relaxed mb-8">{t.detail3}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contacto" className="inline-flex items-center justify-center gap-2 bg-[#B8975A] text-white px-8 py-4 text-sm tracking-wider uppercase hover:bg-[#96784a] transition-colors">
                  {t.requestAssessment}
                  <ArrowRight size={16} />
                </Link>
                <a href="tel:+34952764547" className="inline-flex items-center justify-center gap-2 border border-[#1C1C1A] text-[#1C1C1A] px-8 py-4 text-sm tracking-wider uppercase hover:bg-[#1C1C1A] hover:text-white transition-colors">
                  <Phone size={16} />
                  {t.callNow}
                </a>
              </div>
            </AnimatedSection>
            <AnimatedSection animation="fadeLeft" className="relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 border-t-2 border-l-2 border-[#B8975A]"></div>
              <img src="/images/otoplastia-pre3.jpg" alt="Otoplastia" className="w-full h-auto" />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-2 border-r-2 border-[#B8975A]"></div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-20 bg-[#F0EDE7]">
        <div className="container mx-auto px-6 max-w-5xl">
          <AnimatedSection animation="fadeUp" className="text-center mb-12">
            <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-4">{t.immediateResult}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1C1C1A]">{t.realResults}</h2>
          </AnimatedSection>

          {/* Before photos */}
          <AnimatedSection animation="fadeUp" delay={0.1}>
            <p className="text-center text-xs tracking-[0.2em] uppercase text-[#B8975A] mb-4 font-medium">{t.before}</p>
            <div className="grid grid-cols-3 gap-4 mb-10">
              {['/images/otoplastia-pre1.jpg', '/images/otoplastia-pre2.jpg', '/images/otoplastia-pre3.jpg'].map((src, i) => (
                <div key={i} className="border-2 border-[#B8975A]/30 p-1.5 bg-white">
                  <img src={src} alt={`${t.before} ${i + 1}`} className="w-full h-auto" />
                </div>
              ))}
            </div>
          </AnimatedSection>

          {/* After photos */}
          <AnimatedSection animation="fadeUp" delay={0.2}>
            <p className="text-center text-xs tracking-[0.2em] uppercase text-[#B8975A] mb-4 font-medium">{t.after}</p>
            <div className="grid grid-cols-2 gap-4 max-w-2xl mx-auto">
              {['/images/otoplastia-post1.jpg', '/images/otoplastia-post2.jpg'].map((src, i) => (
                <div key={i} className="border-2 border-[#B8975A]/30 p-1.5 bg-white">
                  <img src={src} alt={`${t.after} ${i + 1}`} className="w-full h-auto" />
                </div>
              ))}
            </div>
          </AnimatedSection>

          <p className="text-center text-[#8A8780] text-sm mt-8">{t.realCases}</p>
        </div>
      </section>

      {/* Beneficios */}
      <section className="py-20 bg-[#1C1C1A]">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection animation="fadeRight">
              <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{t.benefitsTitle}</p>
              <h2 className="font-serif text-4xl text-white mb-8">{t.resultsTitle}</h2>
              <div className="space-y-4">
                {t.benefits.map((beneficio, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-6 h-6 rounded-full bg-[#B8975A]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check size={14} className="text-[#B8975A]" />
                    </div>
                    <span className="text-white/80">{beneficio}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>
            <AnimatedSection animation="fadeLeft">
              <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{t.candidatesTitle}</p>
              <h2 className="font-serif text-4xl text-white mb-8">{t.candidatesQuestion}</h2>
              <div className="space-y-4">
                {t.candidates.map((candidato, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-6 h-6 rounded-full bg-[#B8975A]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check size={14} className="text-[#B8975A]" />
                    </div>
                    <span className="text-white/80">{candidato}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#FDFBF8]">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <AnimatedSection animation="fadeUp">
            <h2 className="font-serif text-4xl text-[#1C1C1A] mb-6">{t.ctaTitle}</h2>
            <p className="text-[#4A4845] mb-10">{t.ctaDesc}</p>
            <Link to="/contacto" className="inline-flex items-center gap-2 bg-[#B8975A] text-white px-10 py-4 text-sm tracking-wider uppercase hover:bg-[#96784a] transition-colors">
              {t.contact}
              <ArrowRight size={16} />
            </Link>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Otoplastia;
